/**
 * A container's expenses - freight, customs, clearance, trucking, whatever
 * the office pays out for it - as many lines as it takes.
 *
 * Each line is a payment voucher out of a cash box or bank, so the box's
 * balance goes down with it and it shows on the Payments tab too; it just
 * also carries the container. The kinds of expense are the expense
 * categories from Accounting, the office's own list - one missing from it
 * is added from here without leaving the window.
 *
 * Like every voucher it is from an account to an account, and both are
 * picked the way the chart is laid out - the main account, then the one
 * under it, or all of them at once: the box under 111 / 113, the kind of
 * expense under 4.
 *
 * Totals are per currency: a box is in one currency, and CNY and USD are
 * never added together.
 */

import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import AddIcon from "@mui/icons-material/Add";

import SuiBox from "components/SuiBox";
import DialogShell, { FieldGrid } from "layouts/accounting/DialogShell";
import { Field, Section } from "layouts/accounting/Fields";
import AccountTreePick, { boxOptions, kindOptions } from "layouts/accounting/AccountTreePick";
import { api, problemOf, money, today } from "layouts/accounting/api";
import ExpensesTable from "./ExpensesTable";

const BLANK = { category_id: "", source_id: "", amount: "", date: "", description: "" };

function ContainerExpensesDialog({ open, container, onClose }) {
  const [rows, setRows] = useState(null);
  const [boxes, setBoxes] = useState([]);
  const [types, setTypes] = useState([]);
  // the boxes and kinds as chart accounts - where each sits in the tree
  const [links, setLinks] = useState([]);
  const [form, setForm] = useState(BLANK);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  // the name of a new kind of expense, while one is being added
  const [newType, setNewType] = useState(null);

  const load = () =>
    api
      .get("/vouchers", { type: "payment", container_id: container.id })
      .then((data) => setRows(Array.isArray(data) ? data : []))
      .catch((e) => {
        setRows([]);
        setError(problemOf(e, "Could not load the expenses"));
      });

  const loadLinks = () =>
    api
      .get("/accounts/postable", { for: "links" })
      .then((data) => setLinks(Array.isArray(data) ? data : []))
      .catch(() => setLinks([]));

  const loadTypes = () =>
    Promise.all([
      api
        .get("/categories")
        .then((data) =>
          setTypes((Array.isArray(data) ? data : []).filter((c) => c.type === "expense"))
        )
        .catch(() => setTypes([])),
      // a kind just added gets its place in the chart on this read
      loadLinks(),
    ]);

  useEffect(() => {
    if (!open || !container) return;
    setRows(null);
    setError("");
    setNewType(null);
    setForm({ ...BLANK, date: today() });
    load();
    loadTypes();
    api
      .get("/boxes")
      .then((data) => setBoxes(Array.isArray(data) ? data : []))
      .catch(() => setBoxes([]));
  }, [open, container]);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  const box = boxes.find((b) => String(b.id) === String(form.source_id));
  const kinds = kindOptions(types, links, "expense").filter(
    (k) => k.is_active || String(k.category_id) === String(form.category_id)
  );
  const boxChoices = boxOptions(
    boxes.filter((b) => b.is_active || String(b.id) === String(form.source_id)),
    links
  );

  const add = async () => {
    if (!form.category_id) return setError("Pick the kind of expense");
    if (!form.source_id) return setError("Pick the cash box or bank it was paid from");
    if (!(Number(form.amount) > 0)) return setError("Type the amount");
    setSaving(true);
    setError("");
    try {
      await api.post("/vouchers", {
        type: "payment",
        container_id: container.id,
        category_id: form.category_id,
        source_id: form.source_id,
        amount: Number(form.amount),
        date: form.date || today(),
        description: form.description,
      });
      // the kind and the box stay picked - the next line is often the same
      set({ amount: "", description: "" });
      await load();
    } catch (e) {
      setError(problemOf(e, "Could not save the expense"));
    }
    setSaving(false);
    return null;
  };

  const remove = async (r) => {
    const what = `${r.category || "this expense"} - ${money(r.amount)} ${r.currency}`;
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Delete ${what}?`)) return;
    try {
      await api.del(`/vouchers/${r.id}`);
      await load();
    } catch (e) {
      setError(problemOf(e, "Could not delete"));
    }
  };

  const addType = async () => {
    const name = String(newType || "").trim();
    if (!name) return;
    try {
      const res = await api.post("/categories", { name, type: "expense" });
      await loadTypes();
      if (res?.id) set({ category_id: res.id });
      setNewType(null);
    } catch (e) {
      setError(problemOf(e, "Could not add the kind"));
    }
  };

  return (
    <DialogShell
      open={open}
      title={`Expenses · مصاريف - ${container?.name || ""}`}
      onClose={onClose}
      error={error}
      maxWidth="md"
    >
      <Section title="New expense · مصروف جديد">
        <AccountTreePick
          mainLabel="From · الحساب الرئيسي"
          label="Paid from · من صندوق"
          options={boxChoices}
          value={form.source_id}
          onChange={(id) => set({ source_id: id })}
          getLabel={(b) => [b.code, b.name].filter(Boolean).join(" ")}
          getHint={(b) => `${money(b.balance)} ${b.currency}`}
          noOptionsText="No boxes - add one under Accounting > Boxes & Banks"
        />
        <SuiBox display="flex" gap={1} alignItems="flex-start">
          <SuiBox flex={1}>
            <AccountTreePick
              mainLabel="To · الحساب الرئيسي"
              label="Kind · النوع"
              options={kinds}
              value={form.category_id ? `c:${form.category_id}` : ""}
              onChange={(id) =>
                set({ category_id: kinds.find((k) => k.id === id)?.category_id || "" })
              }
              noOptionsText="No kinds yet - add one with +"
            />
          </SuiBox>
          <Tooltip title="New kind of expense · نوع جديد">
            <IconButton
              aria-label="New kind of expense"
              onClick={() => setNewType(newType === null ? "" : null)}
              sx={{ mt: 0.5 }}
            >
              <AddIcon />
            </IconButton>
          </Tooltip>
        </SuiBox>

        {newType !== null && (
          <SuiBox display="flex" gap={1} mb={2}>
            <TextField
              size="small"
              autoFocus
              fullWidth
              placeholder="e.g. Customs · جمارك"
              value={newType}
              onChange={(e) => setNewType(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") addType();
                if (e.key === "Escape") setNewType(null);
              }}
            />
            <Button variant="contained" color="primary" onClick={addType}>
              Add kind
            </Button>
          </SuiBox>
        )}

        <FieldGrid columns={3}>
          <Field
            label={`Amount · المبلغ${box ? ` (${box.currency})` : ""}`}
            type="number"
            value={form.amount}
            onChange={(e) => set({ amount: e.target.value })}
            inputProps={{ min: 0, step: "any" }}
          />
          <Field
            label="Date · التاريخ"
            type="date"
            value={form.date}
            onChange={(e) => set({ date: e.target.value })}
          />
          <Field
            label="Note · ملاحظة"
            value={form.description}
            onChange={(e) => set({ description: e.target.value })}
            onKeyDown={(e) => e.key === "Enter" && add()}
          />
        </FieldGrid>
        <SuiBox display="flex" justifyContent="flex-end">
          <Button variant="contained" color="primary" onClick={add} disabled={saving}>
            {saving ? "Saving..." : "Add expense"}
          </Button>
        </SuiBox>
      </Section>

      <Section title="This container's expenses · مصاريف الكونتينر">
        <ExpensesTable rows={rows} onRemove={remove} />
      </Section>
    </DialogShell>
  );
}

ContainerExpensesDialog.defaultProps = { container: null };

ContainerExpensesDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  container: PropTypes.shape({ id: PropTypes.number, name: PropTypes.string }),
  onClose: PropTypes.func.isRequired,
};

export default ContainerExpensesDialog;
